import { safeGet } from './safeGet'

// Data mapping for EfficiencyTestsDetails.vue's "Confronto driver" bubble chart (DriversBubbleChart.vue),
// built from GET /api/efficiencyTests/{id}/result: x/y = the chosen drivers' `driverDetails[].value`
// (matched on `driverDetails[].code === summary.drivers[].driver`), z = the primary result's totalScore.

export const TESTED_PRODUCT_COLOR = '#ea633e'

// The scoreResult shown for the product — same choice as efficiencyTestsDetails' extractTotalScore().
export function getPrimaryScoreResult(scoreObj) {
  const results = safeGet(() => scoreObj.scoreResults, []) || []
  return results.find((x) => x && x.show4Product) || results[0] || null
}

export function getDriverValue(scoreResult, driver) {
  const details = safeGet(() => scoreResult.driverDetails, []) || []
  const detail = details.find(
    (d) => d && d.code != null && String(d.code).toUpperCase() === String(driver).toUpperCase()
  )
  return safeGet(() => detail.value, null)
}

export function isValidNumber(value) {
  return typeof value === 'number' && !isNaN(value)
}

// Drivers by descending weight: the first two are the default X / Y axes.
export function getDefaultAxesDrivers(drivers) {
  const sorted = [...(drivers || [])].filter((d) => d && d.driver).sort((a, b) => (b.weight || 0) - (a.weight || 0))
  return {
    x: safeGet(() => sorted[0].driver, null),
    y: safeGet(() => sorted[1].driver, null),
  }
}

// `count` distinguishable shades of blue, from dark to light.
export function getBlueShades(count) {
  if (count <= 0) return []
  return Array.from({ length: count }, (_, i) => {
    const ratio = count === 1 ? 0 : i / (count - 1)
    return hslToHex(210, 80 - 20 * ratio, 30 + 45 * ratio)
  })
}

export function hslToHex(h, s, l) {
  const sat = s / 100
  const light = l / 100
  const a = sat * Math.min(light, 1 - light)
  const channel = (n) => {
    const k = (n + h / 30) % 12
    const color = light - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, '0')
  }
  return `#${channel(0)}${channel(8)}${channel(4)}`
}

function mapPoint(product, xDriver, yDriver) {
  const scoreResult = getPrimaryScoreResult(safeGet(() => product.score, null))
  const x = getDriverValue(scoreResult, xDriver)
  const y = getDriverValue(scoreResult, yDriver)
  const z = safeGet(() => scoreResult.totalScore, null)
  // A product without a score (an excluded comparable) or missing one of the chosen drivers can't be placed.
  if (!isValidNumber(x) || !isValidNumber(y) || !isValidNumber(z)) return null

  return {
    x,
    y,
    z,
    isin: product.isin,
    issuerName: safeGet(() => product.score.productKey.issuerName, null) || product.issuerCode || '',
    name: safeGet(() => product.score.productKey.name, null) || product.name || '',
  }
}

export function formatScorePercent(value) {
  return Math.round(value * 100) + '%'
}

const byScoreDesc = (a, b) => b.z - a.z

// One series per product (so that the legend lists every ISIN, with its score, in its own colour):
// the tested product in orange, the comparables in shades of blue (darker = higher score). Series are
// sorted by score, highest first, which is also the legend order. Coverage filters never apply here.
export function getBubbleSeries(testedProduct, comparables, xDriver, yDriver) {
  if (!xDriver || !yDriver) return []

  const series = []
  const tested = testedProduct ? mapPoint(testedProduct, xDriver, yDriver) : null
  if (tested) {
    series.push({ point: tested, color: TESTED_PRODUCT_COLOR, isTestedProduct: true, zIndex: 2 })
  }

  const points = (comparables || [])
    .map((c) => mapPoint(c, xDriver, yDriver))
    .filter((p) => p != null)
    .sort(byScoreDesc)
  const shades = getBlueShades(points.length)
  points.forEach((point, i) => {
    series.push({ point, color: shades[i], isTestedProduct: false, zIndex: 1 })
  })

  return series
    .sort((a, b) => byScoreDesc(a.point, b.point))
    .map(({ point, ...s }, i) => ({
      ...s,
      name: `${point.isin} (${formatScorePercent(point.z)})`,
      legendIndex: i,
      data: [point],
    }))
}
